import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { kbWebScraper } from '../api';
import LoadingSpinner from './LoadingSpinner';
import { MonitorCard, monitorInfo } from './WebsiteMonitorParts';
import {
  Radar, Plus, X, Search, Mail, Send, Pencil, Trash2, Globe, ChevronDown, ChevronRight, XCircle, CheckCircle,
  AlertTriangle, BellRing, ExternalLink
} from 'lucide-react';

// Watch websites for changes and email a list of people when something important changes.
// Each website is a KB Web Scraper saved site, so it can be synced into an agent's KB later.

const cardClass = 'bg-card/95 backdrop-blur-md border border-border/60 rounded-xl p-6 shadow-elegant';
const inputClass = 'w-full px-4 py-3 bg-muted/30 border border-border/60 rounded-lg text-foreground placeholder-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/50 focus:border-primary transition-all duration-300';
const primaryBtn = 'px-6 py-3 bg-gradient-button text-primary-foreground rounded-lg hover:shadow-lg hover:shadow-primary/20 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-300 flex items-center justify-center gap-2 font-medium btn-hover-gradient';
const secondaryBtn = 'px-4 py-2 rounded-lg text-sm font-medium border border-border/60 text-foreground hover:border-primary/40 hover:bg-muted/40 transition-all duration-300 flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed';
const iconBtn = 'p-2 rounded-lg text-muted-foreground hover:text-primary hover:bg-muted/40 transition-colors disabled:opacity-50';

const STATUS_CHIP = {
  changed: { label: 'Changed', className: 'bg-warning/20 text-warning' },
  minor_changes: { label: 'Minor edits', className: 'bg-muted/60 text-muted-foreground' },
  unchanged: { label: 'No changes', className: 'bg-success/15 text-success' },
  baseline: { label: 'No changes', className: 'bg-success/15 text-success' },
  unmonitorable: { label: 'Cannot watch', className: 'bg-muted/60 text-muted-foreground' },
  error: { label: 'Check failed', className: 'bg-destructive/15 text-destructive' },
};

const errorText = (e) => {
  const detail = e?.response?.data?.detail;
  return typeof detail === 'string' ? detail : (e?.message || 'Something went wrong');
};

const formatDate = (iso) => (iso ? new Date(iso).toLocaleString() : 'Never');

// Email chips: type an address and press Enter, comma or space
const EmailInput = ({ emails, onChange }) => {
  const [draft, setDraft] = useState('');
  const commit = () => {
    const parts = draft.split(/[\s,;]+/).map((p) => p.trim()).filter(Boolean);
    if (!parts.length) return;
    onChange([...emails, ...parts.filter((p) => !emails.includes(p.toLowerCase())).map((p) => p.toLowerCase())]);
    setDraft('');
  };
  return (
    <div className="flex flex-wrap items-center gap-2 px-3 py-2 bg-muted/30 border border-border/60 rounded-lg focus-within:ring-2 focus-within:ring-primary/50">
      {emails.map((e) => (
        <span key={e} className="flex items-center gap-1 text-xs px-2 py-1 rounded-full bg-primary/15 text-foreground">
          <Mail className="w-3 h-3 text-primary" /> {e}
          <button type="button" onClick={() => onChange(emails.filter((x) => x !== e))} aria-label={`Remove ${e}`}><X className="w-3 h-3" /></button>
        </span>
      ))}
      <input
        value={draft}
        onChange={(e) => setDraft(e.target.value)}
        onKeyDown={(e) => {
          if (['Enter', ',', ' ', ';'].includes(e.key)) { e.preventDefault(); commit(); }
          if (e.key === 'Backspace' && !draft && emails.length) onChange(emails.slice(0, -1));
        }}
        onBlur={commit}
        placeholder={emails.length ? 'Add another' : 'name@company.com'}
        className="flex-1 min-w-[180px] bg-transparent py-1 text-sm text-foreground placeholder-muted-foreground focus:outline-none"
      />
    </div>
  );
};

// Page picker over the free sitemap + link discovery
const PagePicker = ({ url, selected, onChange, maxPages }) => {
  const [loading, setLoading] = useState(false);
  const [found, setFound] = useState(null);
  const [filter, setFilter] = useState('');
  const [error, setError] = useState('');

  const discover = async () => {
    setLoading(true);
    setError('');
    try {
      const r = await kbWebScraper.discover(url);
      const pages = r.data.pages || [];
      const known = new Set(pages.map((p) => p.url));
      setFound([...selected.filter((p) => !known.has(p.url)), ...pages]);
    } catch (e) {
      setError(errorText(e));
    } finally {
      setLoading(false);
    }
  };

  const chosen = new Set(selected.map((p) => p.url));
  const toggle = (page) => onChange(chosen.has(page.url) ? selected.filter((p) => p.url !== page.url)
    : [...selected, { url: page.url, name: page.name }]);
  const visible = (found || []).filter((p) => !filter || `${p.name} ${p.url}`.toLowerCase().includes(filter.toLowerCase()));

  return (
    <div>
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <span className="text-sm text-foreground">{selected.length} page{selected.length === 1 ? '' : 's'} watched</span>
        <button type="button" onClick={discover} disabled={!url || loading} className={secondaryBtn}>
          {loading ? <LoadingSpinner size="sm" /> : <Search className="w-4 h-4" />} {found ? 'Find pages again' : 'Pick more pages'}
        </button>
      </div>
      {error && <p className="text-xs text-destructive mt-2">{error}</p>}
      {loading && <p className="text-xs text-muted-foreground mt-2">Reading the sitemap and following links. This takes up to 40 seconds.</p>}
      {!found && selected.length > 0 && (
        <ul className="mt-2 text-xs text-muted-foreground space-y-0.5">
          {selected.slice(0, 5).map((p) => <li key={p.url} className="truncate">{p.url}</li>)}
          {selected.length > 5 && <li>and {selected.length - 5} more</li>}
        </ul>
      )}
      {found && (
        <div className="mt-3">
          <div className="relative mb-2">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
            <input value={filter} onChange={(e) => setFilter(e.target.value)} placeholder="Filter pages, e.g. pricing"
              className="w-full pl-9 pr-3 py-2 text-sm bg-muted/30 border border-border/60 rounded-lg text-foreground focus:outline-none focus:ring-2 focus:ring-primary/50" />
          </div>
          <div className="border border-border/60 rounded-lg divide-y divide-border/40 overflow-y-auto" style={{ maxHeight: '260px' }}>
            {visible.map((p) => {
              const checked = chosen.has(p.url);
              const atLimit = !checked && selected.length >= maxPages;
              return (
                <label key={p.url} className={`flex items-start gap-3 px-3 py-2 cursor-pointer hover:bg-muted/30 ${atLimit ? 'opacity-50' : ''}`}>
                  <input type="checkbox" className="mt-1 w-4 h-4 accent-[hsl(var(--primary))]" checked={checked} disabled={atLimit} onChange={() => toggle(p)} />
                  <span className="min-w-0 flex-1">
                    <span className="block text-sm text-foreground truncate">{p.name}</span>
                    <span className="block text-xs text-muted-foreground truncate">{p.url.replace(/^https?:\/\//, '')}</span>
                  </span>
                </label>
              );
            })}
            {!visible.length && <div className="p-3 text-sm text-muted-foreground">No pages match this filter.</div>}
          </div>
        </div>
      )}
    </div>
  );
};

const AddWebsite = ({ onAdded, onCancel, maxPages }) => {
  const [url, setUrl] = useState('');
  const [name, setName] = useState('');
  const [pages, setPages] = useState([]);
  const [emails, setEmails] = useState([]);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  // Until pages are picked, the page at the URL itself is watched
  const cleanUrl = url.trim() && (/^https?:\/\//i.test(url.trim()) ? url.trim() : `https://${url.trim()}`);

  const save = async () => {
    setSaving(true);
    setError('');
    try {
      const r = await kbWebScraper.createSite({ url: cleanUrl, name: name.trim() || null, pages: pages.length ? pages : null, emails });
      onAdded(r.data);
    } catch (e) {
      setError(errorText(e));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className={`${cardClass} mb-6`}>
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-semibold text-foreground flex items-center gap-2"><Plus className="w-5 h-5 text-primary" /> Add a website</h3>
        <button onClick={onCancel} className={iconBtn} aria-label="Cancel"><X className="w-4 h-4" /></button>
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-foreground mb-2">Website URL</label>
            <input className={inputClass} value={url} onChange={(e) => { setUrl(e.target.value); setPages([]); }} placeholder="example.com/pricing" />
          </div>
          <div>
            <label className="block text-sm font-medium text-foreground mb-2">Name (optional)</label>
            <input className={inputClass} value={name} onChange={(e) => setName(e.target.value)} placeholder="Detected from the URL" />
          </div>
          <div>
            <label className="block text-sm font-medium text-foreground mb-2">Email these people when it changes</label>
            <EmailInput emails={emails} onChange={setEmails} />
            <p className="text-xs text-muted-foreground mt-1">One email per new important change, not a daily reminder. You can leave this empty and only see changes here.</p>
          </div>
        </div>
        <div>
          <label className="block text-sm font-medium text-foreground mb-2">Pages to watch</label>
          {!pages.length && cleanUrl && <p className="text-xs text-muted-foreground mb-2">Only {cleanUrl.replace(/^https?:\/\//, '')} is watched unless you pick more pages.</p>}
          <PagePicker url={cleanUrl} selected={pages} onChange={setPages} maxPages={maxPages} />
        </div>
      </div>
      {error && <p className="text-sm text-destructive mt-4">{error}</p>}
      <div className="flex justify-end gap-2 mt-6">
        <button onClick={onCancel} className={secondaryBtn}>Cancel</button>
        <button onClick={save} disabled={!cleanUrl || saving} className={primaryBtn}>
          {saving ? <LoadingSpinner size="sm" /> : <Radar className="w-4 h-4" />} Start watching
        </button>
      </div>
    </div>
  );
};

const EditWebsite = ({ site, onSaved, onCancel, maxPages }) => {
  const [name, setName] = useState(site.name);
  const [pages, setPages] = useState(site.pages || []);
  const [emails, setEmails] = useState(site.monitor_emails || []);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const save = async () => {
    setSaving(true);
    setError('');
    try {
      const r = await kbWebScraper.updateSite(site.id, { name: name.trim(), pages, monitor_emails: emails });
      onSaved(r.data);
    } catch (e) {
      setError(errorText(e));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="mt-4 rounded-lg border border-primary/40 bg-primary/5 p-4">
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-foreground mb-2">Name</label>
            <input className={inputClass} value={name} onChange={(e) => setName(e.target.value)} />
          </div>
          <div>
            <label className="block text-sm font-medium text-foreground mb-2">Email these people when it changes</label>
            <EmailInput emails={emails} onChange={setEmails} />
          </div>
        </div>
        <div>
          <label className="block text-sm font-medium text-foreground mb-2">Pages to watch</label>
          <PagePicker url={site.base_url} selected={pages} onChange={setPages} maxPages={maxPages} />
          <p className="text-xs text-muted-foreground mt-2">These are also the pages a KB Web Scraper sync scrapes.</p>
        </div>
      </div>
      {error && <p className="text-sm text-destructive mt-3">{error}</p>}
      <div className="flex justify-end gap-2 mt-4">
        <button onClick={onCancel} className={secondaryBtn}>Cancel</button>
        <button onClick={save} disabled={saving || !pages.length || !name.trim()} className={primaryBtn}>
          {saving ? <LoadingSpinner size="sm" /> : <CheckCircle className="w-4 h-4" />} Save
        </button>
      </div>
    </div>
  );
};

const WebsiteMonitoring = () => {
  const navigate = useNavigate();
  const [sites, setSites] = useState([]);
  const [loading, setLoading] = useState(true);
  const [config, setConfig] = useState({ email_configured: true, max_pages: 100 });
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  const [adding, setAdding] = useState(false);
  const [expanded, setExpanded] = useState(null);
  const [editing, setEditing] = useState(null);
  const [busy, setBusy] = useState('');
  const [confirmDelete, setConfirmDelete] = useState(null);

  const load = async () => {
    try {
      const r = await kbWebScraper.listSites();
      const list = Array.isArray(r.data) ? r.data : [];
      // Expanded rows need the full change details, which the list leaves out
      setSites((prev) => list.map((s) => {
        const old = prev.find((p) => p.id === s.id);
        return old?.monitor_changes && old.monitor_checked_at === s.monitor_checked_at ? { ...s, monitor_changes: old.monitor_changes } : s;
      }));
    } catch (e) {
      setError(errorText(e));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    kbWebScraper.getConfig().then((r) => setConfig((c) => ({ ...c, ...r.data }))).catch(() => {});
  }, []);

  // Refresh while a check runs
  useEffect(() => {
    if (!sites.some((s) => s.monitor_checking)) return undefined;
    const timer = setTimeout(async () => {
      await load();
      if (expanded) loadDetails(expanded);
    }, 4000);
    return () => clearTimeout(timer);
  }, [sites, expanded]);

  const loadDetails = async (siteId) => {
    try {
      const r = await kbWebScraper.getSite(siteId);
      setSites((prev) => prev.map((s) => (s.id === siteId ? { ...s, ...r.data } : s)));
    } catch (e) {
      setError(errorText(e));
    }
  };

  const toggleExpand = (site) => {
    if (expanded === site.id) { setExpanded(null); return; }
    setExpanded(site.id);
    loadDetails(site.id);
  };

  const act = async (site, request, message) => {
    setBusy(site.id);
    setError('');
    try {
      const r = await request();
      if (message) setNotice(typeof message === 'function' ? message(r) : message);
      await load();
      if (expanded === site.id) loadDetails(site.id);
    } catch (e) {
      setError(errorText(e));
    } finally {
      setBusy('');
    }
  };

  const sorted = useMemo(() => {
    const rank = { changed: 0, minor_changes: 1 };
    return [...sites].sort((a, b) => (rank[a.monitor_status] ?? 2) - (rank[b.monitor_status] ?? 2) || a.name.localeCompare(b.name));
  }, [sites]);
  const changedCount = sites.filter((s) => s.monitor_enabled && s.monitor_status === 'changed').length;

  return (
    <div className="flex-1 flex flex-col min-h-0">
      <div className="mb-6 flex items-start justify-between gap-4 flex-wrap">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-lg bg-gradient-button flex items-center justify-center shadow-md">
            <Radar className="w-5 h-5 text-primary-foreground" />
          </div>
          <div>
            <h1 className="text-3xl font-bold text-foreground mb-1">Website Monitoring</h1>
            <p className="text-muted-foreground">Know when a website your agents learn from changes, and who to tell.</p>
          </div>
        </div>
        {!adding && <button onClick={() => setAdding(true)} className={primaryBtn}><Plus className="w-4 h-4" /> Add website</button>}
      </div>

      {!config.email_configured && (
        <div className="mb-4 flex items-start gap-3 p-4 rounded-lg border border-warning/40 bg-warning/10 text-foreground text-sm">
          <AlertTriangle className="w-5 h-5 flex-shrink-0 text-warning" />
          Email is not set up on this server (SMTP_USER and SMTP_PASSWORD), so changes show here but no emails go out.
        </div>
      )}
      {error && (
        <div className="mb-4 flex items-start gap-3 p-4 rounded-lg border border-destructive/30 bg-destructive/10 text-destructive text-sm">
          <XCircle className="w-5 h-5 flex-shrink-0" /><div className="flex-1">{error}</div>
          <button onClick={() => setError('')} aria-label="Dismiss"><X className="w-4 h-4" /></button>
        </div>
      )}
      {notice && (
        <div className="mb-4 flex items-start gap-3 p-4 rounded-lg border border-success/30 bg-success/10 text-foreground text-sm">
          <CheckCircle className="w-5 h-5 flex-shrink-0 text-success" /><div className="flex-1">{notice}</div>
          <button onClick={() => setNotice('')} aria-label="Dismiss"><X className="w-4 h-4" /></button>
        </div>
      )}

      {adding && (
        <AddWebsite
          maxPages={config.max_pages}
          onCancel={() => setAdding(false)}
          onAdded={(site) => {
            setAdding(false);
            setNotice(`Watching ${site.name}. The first reading takes a minute; after that it is checked once a day.`);
            load();
          }}
        />
      )}

      <div className={cardClass}>
        <div className="flex items-center justify-between gap-3 mb-2 flex-wrap">
          <h3 className="text-lg font-semibold text-foreground">Watched websites</h3>
          {changedCount > 0 && (
            <span className="text-xs px-2 py-1 rounded-full bg-warning/20 text-warning flex items-center gap-1">
              <BellRing className="w-3.5 h-3.5" /> {changedCount} changed
            </span>
          )}
        </div>
        <p className="text-xs text-muted-foreground mb-4">
          Each website is read once a day for free. When text changes, AI reads only the changed lines and decides whether it matters to an agent; prices, phone numbers, emails and opening hours always count. Important changes are emailed once to the people listed.
        </p>

        {loading ? <LoadingSpinner size="sm" text="Loading..." /> : !sites.length ? (
          <p className="text-sm text-muted-foreground">No websites yet. Add one, or save a website in KB Web Scraper and it shows up here.</p>
        ) : (
          <div className="divide-y divide-border/40">
            {sorted.map((site) => {
              const chip = !site.monitor_enabled ? { label: 'Not watching', className: 'bg-muted/60 text-muted-foreground' }
                : site.monitor_checking ? { label: 'Checking', className: 'bg-primary/15 text-primary' }
                  : STATUS_CHIP[site.monitor_status] || { label: 'Waiting for first check', className: 'bg-muted/60 text-muted-foreground' };
              const open = expanded === site.id;
              return (
                <div key={site.id} className="py-4">
                  <div className="flex items-start gap-3 flex-wrap">
                    <button onClick={() => toggleExpand(site)} className="mt-1 text-muted-foreground hover:text-foreground" aria-label={open ? 'Collapse' : 'Expand'}>
                      {open ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
                    </button>
                    <div className="flex-1 min-w-[260px]">
                      <div className="flex items-center gap-2 flex-wrap">
                        <button onClick={() => toggleExpand(site)} className="font-medium text-foreground hover:text-primary text-left">{site.name}</button>
                        <span className={`text-xs px-2 py-0.5 rounded-full ${chip.className}`}>{chip.label}</span>
                      </div>
                      <a href={site.base_url} target="_blank" rel="noreferrer" className="text-xs text-primary break-all inline-flex items-center gap-1">
                        {site.base_url} <ExternalLink className="w-3 h-3" />
                      </a>
                      <div className={`text-xs mt-1 ${monitorInfo(site).className}`}>{monitorInfo(site).text}</div>
                      <div className="flex items-center gap-1.5 flex-wrap mt-2">
                        <Mail className="w-3.5 h-3.5 text-muted-foreground" />
                        {site.monitor_emails?.length ? site.monitor_emails.map((e) => (
                          <span key={e} className="text-xs px-2 py-0.5 rounded-full bg-muted/60 text-foreground">{e}</span>
                        )) : <span className="text-xs text-muted-foreground">No email alerts</span>}
                        {site.monitor_notified_at && <span className="text-[11px] text-muted-foreground">· last emailed {formatDate(site.monitor_notified_at)}</span>}
                      </div>
                      <div className="text-[11px] text-muted-foreground mt-1">
                        {site.pages.length} page{site.pages.length === 1 ? '' : 's'} watched · checked {formatDate(site.monitor_checked_at)}
                      </div>
                    </div>
                    <div className="flex items-center gap-1">
                      <button
                        onClick={() => act(site, () => kbWebScraper.sendMonitorTestEmail(site.id), (r) => `Test email sent to ${r.data.sent.join(', ')}.`)}
                        disabled={busy === site.id || !site.monitor_emails?.length}
                        className={iconBtn}
                        title="Send a test email"
                      >
                        <Send className="w-4 h-4" />
                      </button>
                      <button onClick={() => { setEditing(editing === site.id ? null : site.id); }} className={iconBtn} title="Edit name, pages and emails">
                        <Pencil className="w-4 h-4" />
                      </button>
                      <button onClick={() => navigate('/admin?section=kb-web-scraper')} className={iconBtn} title="Open KB Web Scraper to sync this website into an agent's knowledge base">
                        <Globe className="w-4 h-4" />
                      </button>
                      {confirmDelete === site.id ? (
                        <span className="flex items-center gap-1 text-xs">
                          <button onClick={() => act(site, () => kbWebScraper.deleteSite(site.id), `Removed ${site.name}.`).then(() => setConfirmDelete(null))}
                            className="px-2 py-1 rounded bg-destructive text-destructive-foreground">Remove</button>
                          <button onClick={() => setConfirmDelete(null)} className="px-2 py-1 rounded border border-border/60">Keep</button>
                        </span>
                      ) : (
                        <button onClick={() => setConfirmDelete(site.id)} className={`${iconBtn} hover:text-destructive`}
                          title="Remove this website. It is also removed from KB Web Scraper; files already in an agent's KB stay.">
                          <Trash2 className="w-4 h-4" />
                        </button>
                      )}
                    </div>
                  </div>

                  {editing === site.id && (
                    <EditWebsite
                      site={site}
                      maxPages={config.max_pages}
                      onCancel={() => setEditing(null)}
                      onSaved={() => { setEditing(null); setNotice(`Saved ${site.name}.`); load(); }}
                    />
                  )}

                  {open && (
                    <div className="mt-4">
                      <MonitorCard
                        site={site}
                        busy={busy === site.id}
                        onCheck={() => act(site, () => kbWebScraper.checkSiteNow(site.id))}
                        onSeen={() => act(site, () => kbWebScraper.markSiteSeen(site.id), `Marked ${site.name} as seen.`)}
                        onToggle={(enabled) => act(site, () => kbWebScraper.updateSite(site.id, { monitor_enabled: enabled }))}
                        onSync={() => navigate('/admin?section=kb-web-scraper')}
                        syncLabel="Sync in KB Web Scraper"
                        canSync
                      />
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

export default WebsiteMonitoring;
